/**
 * Unified parser for AI stream responses.
 * Handles logs, status updates, and plan updates.
 */

export const createStreamState = () => ({
    buffer: '',
    inLog: false,
    logIndex: null
});

export const chatHelpers = {
    appendAssistantText: (arr, text) => {
        if (!text) return arr;
        const last = arr[arr.length - 1];
        if (last && last.role === 'assistant' && (last.type === 'text' || !last.type)) {
            arr[arr.length - 1] = { ...last, content: (last.content || '') + text };
        } else {
            arr.push({ role: 'assistant', type: 'text', content: text });
        }
        return arr;
    },

    pushStatus: (arr, statusText) => {
        if (!statusText) return arr;
        arr.push({ role: 'assistant', type: 'status', content: statusText });
        return arr;
    },

    startLogBlock: (arr) => {
        arr.push({ role: 'assistant', type: 'log', content: '', isStreaming: true });
        return arr.length - 1;
    },

    closeLogBlock: (arr, idx) => {
        if (idx == null || idx < 0 || idx >= arr.length) return { removed: false };
        const msg = arr[idx];
        if (!msg.content || !msg.content.trim()) {
            arr.splice(idx, 1);
            return { removed: true };
        }
        arr[idx] = { ...msg, isStreaming: false };
        return { removed: false };
    }
};

export const findJsonEndIndex = (str) => {
    if (!str || str.length === 0) return -1;
    let stack = [];
    let inString = false;
    let escaped = false;
    let started = false;
    for (let i = 0; i < str.length; i++) {
        const char = str[i];
        if (inString) {
            if (escaped) escaped = false;
            else if (char === '\\') escaped = true;
            else if (char === '"') inString = false;
            continue;
        }
        if (char === '"') { inString = true; continue; }
        if (char === '{' || char === '[') { stack.push(char === '{' ? '}' : ']'); started = true; }
        else if (char === '}' || char === ']') {
            if (stack.length === 0) return -1;
            const expected = stack.pop();
            if (char !== expected) return -1;
            if (stack.length === 0 && started) return i + 1;
        }
    }
    return -1;
};

export const processStreamChunk = (incomingChunk, state, chat, actions) => {
    const { onPlanGenerated, onPlanUpdate } = actions;
    const { appendAssistantText, pushStatus, startLogBlock, closeLogBlock } = chatHelpers;

    // Очистка тегов целей
    let chunk = incomingChunk.replace('[[TARGET:ZERODAY]]', '').replace('[[TARGET:HACKER]]', '');
    let newChat = [...chat];
    let { buffer, inLog, logIndex } = state;

    buffer += chunk;

    const tryParseJson = (s) => {
        try { return JSON.parse(s); }
        catch (e) { return null; }
    };

    while (buffer.length > 0) {
        const markers = [
            { key: 'LOG_START', token: '[[SHT_LOG_START]]' },
            { key: 'LOG_END', token: '[[SHT_LOG_END]]' },
            { key: 'SET_PLAN', token: '[[SET_PLAN:' },
            { key: 'PLAN_UPDATE', token: '[[PLAN_UPDATE:' },
            { key: 'STATUS', token: '[SHT:' }
        ];

        let nearest = null;
        for (const m of markers) {
            const idx = buffer.indexOf(m.token);
            if (idx !== -1) {
                if (!nearest || idx < nearest.idx) nearest = { ...m, idx };
            }
        }

        if (!nearest) {
            let partialTagFound = false;
            const possibleTags = ['[[', '[SHT'];
            for (const pt of possibleTags) {
                const lastIndex = buffer.lastIndexOf(pt);
                if (lastIndex !== -1 && (buffer.length - lastIndex) < 20) {
                    const safeContent = buffer.slice(0, lastIndex);
                    if (safeContent) {
                        if (inLog && logIndex != null && newChat[logIndex]) {
                            newChat[logIndex] = { ...newChat[logIndex], content: (newChat[logIndex].content || '') + safeContent };
                        } else {
                            newChat = appendAssistantText(newChat, safeContent);
                        }
                    }
                    buffer = buffer.slice(lastIndex);
                    partialTagFound = true;
                    break;
                }
            }
            if (partialTagFound) break;

            if (inLog) {
                if (logIndex === null || !newChat[logIndex] || newChat[logIndex].type !== 'log') {
                    logIndex = startLogBlock(newChat);
                }
                newChat[logIndex] = { ...newChat[logIndex], content: (newChat[logIndex].content || '') + buffer };
            } else {
                newChat = appendAssistantText(newChat, buffer);
            }
            buffer = '';
            break;
        }

        if (nearest.idx > 0) {
            const textBefore = buffer.slice(0, nearest.idx);
            if (inLog) {
                if (logIndex === null || !newChat[logIndex]) logIndex = startLogBlock(newChat);
                newChat[logIndex] = { ...newChat[logIndex], content: (newChat[logIndex].content || '') + textBefore };
            } else {
                newChat = appendAssistantText(newChat, textBefore);
            }
            buffer = buffer.slice(nearest.idx);
            continue;
        }

        if (nearest.key === 'LOG_START') {
            if (inLog) closeLogBlock(newChat, logIndex);
            inLog = true;
            logIndex = startLogBlock(newChat);
            buffer = buffer.slice(nearest.token.length);
            continue;
        }

        if (nearest.key === 'LOG_END') {
            if (inLog) {
                closeLogBlock(newChat, logIndex);
                inLog = false;
                logIndex = null;
            }
            buffer = buffer.slice(nearest.token.length);
            continue;
        }

        if (nearest.key === 'STATUS') {
            const closeBracket = buffer.indexOf(']');
            if (closeBracket === -1) break;
            const inside = buffer.slice(nearest.token.length, closeBracket).trim();
            newChat = pushStatus(newChat, inside);
            buffer = buffer.slice(closeBracket + 1);
            continue;
        }

        if (nearest.key === 'SET_PLAN' || nearest.key === 'PLAN_UPDATE') {
            const prefixLen = nearest.token.length;
            const potentialJson = buffer.slice(prefixLen);
            const jsonEnd = findJsonEndIndex(potentialJson);

            if (jsonEnd !== -1) {
                const payload = potentialJson.slice(0, jsonEnd);
                const data = tryParseJson(payload);
                if (data) {
                    if (nearest.key === 'SET_PLAN' && onPlanGenerated) onPlanGenerated(data);
                    if (nearest.key === 'PLAN_UPDATE' && onPlanUpdate) onPlanUpdate(data);

                    let advance = prefixLen + jsonEnd;
                    if (buffer.substring(advance, advance + 2) === ']]') advance += 2;
                    buffer = buffer.slice(advance);
                    continue;
                }
            } else {
                break;
            }
            if (buffer.length > 50000) buffer = "";
            break;
        }
    }

    return {
        chat: newChat,
        state: { buffer, inLog, logIndex }
    };
};

export const finalizeStream = (chat, state, actions) => {
    const { appendAssistantText } = actions;
    const newChat = [...chat];
    const { inLog, logIndex, buffer } = state;

    if (buffer && !inLog) {
        appendAssistantText(newChat, buffer);
    }

    if (inLog && logIndex !== null) {
        const msg = newChat[logIndex];
        if (msg && (!msg.content || !msg.content.trim())) {
            newChat.splice(logIndex, 1);
        } else if (msg) {
            newChat[logIndex] = { ...msg, isStreaming: false };
        }
    }

    return newChat;
};

// ============================================================================
// ZERO-DAY METHOD DEDICATED STREAM PARSER
// ============================================================================

export const createZeroDayStreamState = () => ({
    buffer: '',
    inLog: false,
    currentToolId: null,
    currentTextId: null,
    logLineBuffer: '',
    isWritingText: false
});

export const processZeroDayStreamChunk = (incomingChunk, state, messages, callbacks = {}) => {
    const { onPlanGenerated, onPlanUpdate } = callbacks;

    // Clean target prefixes
    let chunk = (incomingChunk || '').replace(/\[\[TARGET:(?:ZERODAY|HACKER)\]\]/g, '');
    state.buffer += chunk;

    let newMessages = [...messages];
    let { inLog, currentToolId } = state;

    const getActiveToolId = () => {
        if (currentToolId) return currentToolId;
        // 1. Reconnect to any tool marked 'running'
        const runningTool = [...newMessages].reverse().find(m => m.type === 'tool' && m.status === 'running');
        if (runningTool) {
            currentToolId = runningTool.id;
            return currentToolId;
        }
        // 2. Reconnect to the latest tool card
        const latestTool = [...newMessages].reverse().find(m => m.type === 'tool');
        if (latestTool) {
            currentToolId = latestTool.id;
            newMessages = newMessages.map(m => m.id === latestTool.id ? { ...m, status: 'running' } : m);
            return currentToolId;
        }
        // 3. Fallback: create tool card if none exists
        const now = new Date();
        const timeStr = now.toTimeString().split(' ')[0].substring(0, 5);
        const newToolId = `tool-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
        currentToolId = newToolId;
        newMessages.push({
            id: newToolId,
            role: 'agent',
            type: 'tool',
            toolName: 'tool',
            status: 'running',
            logs: [{ type: 'info', text: `[*] Выполнение...` }],
            time: timeStr
        });
        return currentToolId;
    };

    const appendAgentText = (txt) => {
        if (!txt || inLog) return;
        // Strip xml tags like <think>...</think>, <call:...>, <plan>, etc.
        let clean = txt
            .replace(/<think>[\s\S]*?<\/think>/gi, '')
            .replace(/<\/?(?:call|think|resoaning|reasoning|plan)[^>]*>/gi, '')
            .replace(/call:\w+/gi, '');
        if (!clean.trim()) return;

        state.isWritingText = true;

        // When the agent writes conversational text, finalize any running tool
        if (currentToolId) {
            appendToolLog('', true);
            const tid = currentToolId;
            newMessages = newMessages.map(m => (m.type === 'tool' && m.id === tid) ? { ...m, status: 'success' } : m);
            currentToolId = null;
        }

        // 1. Try to append to the active stream bubble (currentTextId)
        let targetIdx = -1;
        if (state.currentTextId) {
            targetIdx = newMessages.findIndex(m => m.id === state.currentTextId);
            // Fallback only if currentTextId was already active for this turn
            if (targetIdx === -1 && newMessages.length > 0) {
                const lastIdx = newMessages.length - 1;
                const lastMsg = newMessages[lastIdx];
                if (lastMsg.id !== 'init-1' && !lastMsg.id.startsWith('init-') && lastMsg.role === 'agent' && (lastMsg.type === 'text' || !lastMsg.type)) {
                    targetIdx = lastIdx;
                    state.currentTextId = lastMsg.id;
                }
            }
        }

        if (targetIdx !== -1) {
            newMessages[targetIdx] = {
                ...newMessages[targetIdx],
                content: newMessages[targetIdx].content + clean
            };
            return;
        }

        // Otherwise create a fresh dedicated agent message bubble
        const now = new Date();
        const timeStr = now.toTimeString().split(' ')[0].substring(0, 5);
        const newId = `ai-text-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
        state.currentTextId = newId;
        newMessages.push({
            id: newId,
            role: 'agent',
            type: 'text',
            content: clean,
            time: timeStr
        });
    };

    const appendToolLog = (logText, isFinal = false) => {
        const targetId = getActiveToolId();
        if (!targetId) return;
        state.logLineBuffer = (state.logLineBuffer || '') + (logText || '');
        if (!state.logLineBuffer) return;

        let lines = [];
        if (isFinal) {
            lines = state.logLineBuffer.split('\n').map(l => l.replace(/\r$/, '')).filter(l => l.trim().length > 0);
            state.logLineBuffer = '';
        } else {
            const lastNewline = state.logLineBuffer.lastIndexOf('\n');
            if (lastNewline !== -1) {
                const complete = state.logLineBuffer.slice(0, lastNewline);
                state.logLineBuffer = state.logLineBuffer.slice(lastNewline + 1);
                lines = complete.split('\n').map(l => l.replace(/\r$/, '')).filter(l => l.trim().length > 0);
            }
        }
        if (lines.length === 0) return;

        newMessages = newMessages.map(m => {
            if (m.id === targetId) {
                const formatted = lines.map(line => {
                    let type = 'info';
                    const trimmed = line.trim();
                    if (trimmed.startsWith('[>]') || trimmed.startsWith('$')) type = 'cmd';
                    else if (trimmed.startsWith('[+]') || trimmed.startsWith('[✓]') || trimmed.startsWith('SUCCESS:')) type = 'success';
                    else if (trimmed.startsWith('[!]') || trimmed.startsWith('WARN:') || trimmed.startsWith('ERROR:')) type = 'warn';
                    return { type, text: line };
                });
                return { ...m, logs: [...(m.logs || []), ...formatted] };
            }
            return m;
        });
    };

    while (state.buffer.length > 0) {
        const markers = [
            { key: 'LOG_START', token: '[[SHT_LOG_START]]' },
            { key: 'LOG_END', token: '[[SHT_LOG_END]]' },
            { key: 'SET_PLAN', token: '[[SET_PLAN:' },
            { key: 'PLAN_UPDATE', token: '[[PLAN_UPDATE:' },
            { key: 'TOOL_START', token: '[SHT:' },
            { key: 'SYSTEM', token: '[SYSTEM:' }
        ];

        let nearest = null;
        for (const m of markers) {
            let idx = -1;
            if (m.key === 'TOOL_START') {
                // Must match [SHT: but NOT [[SHT_
                let searchPos = 0;
                while (searchPos < state.buffer.length) {
                    const found = state.buffer.indexOf(m.token, searchPos);
                    if (found === -1) break;
                    if (found > 0 && state.buffer[found - 1] === '[') {
                        searchPos = found + 1;
                        continue;
                    }
                    idx = found;
                    break;
                }
            } else {
                idx = state.buffer.indexOf(m.token);
            }
            if (idx !== -1) {
                if (!nearest || idx < nearest.idx) nearest = { ...m, idx };
            }
        }

        if (!nearest) {
            // Safe partial tag check at the tail
            let partialFound = false;
            const partialTokens = ['[[', '[SHT', '[SYS'];
            for (const pt of partialTokens) {
                const lastIdx = state.buffer.lastIndexOf(pt);
                if (lastIdx !== -1 && (state.buffer.length - lastIdx) < 25) {
                    const safe = state.buffer.slice(0, lastIdx);
                    if (safe) {
                        if (inLog) appendToolLog(safe);
                        else appendAgentText(safe);
                    }
                    state.buffer = state.buffer.slice(lastIdx);
                    partialFound = true;
                    break;
                }
            }
            if (partialFound) break;

            if (inLog) {
                appendToolLog(state.buffer);
            } else {
                appendAgentText(state.buffer);
            }
            state.buffer = '';
            break;
        }

        // Output any text that came before the nearest marker
        if (nearest.idx > 0) {
            const textBefore = state.buffer.slice(0, nearest.idx);
            if (inLog) {
                appendToolLog(textBefore);
            } else {
                appendAgentText(textBefore);
            }
            state.buffer = state.buffer.slice(nearest.idx);
            continue;
        }

        // Process marker at index 0
        if (nearest.key === 'LOG_START') {
            inLog = true;
            state.isWritingText = false;
            getActiveToolId();
            state.buffer = state.buffer.slice(nearest.token.length);
            continue;
        }

        if (nearest.key === 'LOG_END') {
            appendToolLog('', true);
            inLog = false;
            if (currentToolId) {
                const targetToolId = currentToolId;
                newMessages = newMessages.map(m => m.id === targetToolId ? { ...m, status: 'success' } : m);
                currentToolId = null;
            }
            state.isWritingText = false;
            state.currentTextId = null;
            state.buffer = state.buffer.slice(nearest.token.length);
            continue;
        }

        if (nearest.key === 'TOOL_START' || nearest.key === 'SYSTEM') {
            const closeBracket = state.buffer.indexOf(']');
            if (closeBracket === -1) {
                // Bracket not closed yet, wait for next chunk
                break;
            }
            const rawContent = state.buffer.slice(nearest.token.length, closeBracket).trim();
            state.buffer = state.buffer.slice(closeBracket + 1);

            if (nearest.key === 'TOOL_START') {
                const toolMatch = rawContent.match(/(?:Выполняю\s+)?(\w+)/);
                const toolName = toolMatch ? toolMatch[1] : 'tool';
                if (['stop'].includes(toolName)) {
                    continue;
                }

                state.currentTextId = null; // Text after a tool must start in a fresh bubble
                state.isWritingText = false;
                if (currentToolId || inLog) {
                    appendToolLog('', true);
                    inLog = false;
                    const prevId = currentToolId;
                    newMessages = newMessages.map(m => (m.type === 'tool' && m.id === prevId) ? { ...m, status: 'success' } : m);
                    currentToolId = null;
                }

                const now = new Date();
                const timeStr = now.toTimeString().split(' ')[0].substring(0, 5);
                const newToolId = `tool-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
                currentToolId = newToolId;
                newMessages.push({
                    id: newToolId,
                    role: 'agent',
                    type: 'tool',
                    toolName: toolName,
                    status: 'running',
                    logs: [{ type: 'info', text: `[*] Инициализация ${toolName}...` }],
                    time: timeStr
                });
            } else {
                // SYSTEM message: create a dedicated separate bubble, NEVER merge into previous AI text bubble!
                state.currentTextId = null;
                state.isWritingText = false;

                // If this is a tool error, also mark the latest tool as errored
                if (rawContent.includes('завершился с ошибкой')) {
                    const latestTool = [...newMessages].reverse().find(m => m.type === 'tool');
                    if (latestTool) {
                        newMessages = newMessages.map(m => m.id === latestTool.id ? {
                            ...m,
                            status: 'error',
                            logs: [...(m.logs || []), { type: 'warn', text: `[!] ${rawContent}` }]
                        } : m);
                    }
                }

                const now = new Date();
                const timeStr = now.toTimeString().split(' ')[0].substring(0, 5);
                const sysId = `sys-msg-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
                newMessages.push({
                    id: sysId,
                    role: 'agent',
                    type: 'text',
                    content: `**[Система]** ${rawContent}`,
                    time: timeStr
                });
            }
            continue;
        }

        if (nearest.key === 'SET_PLAN' || nearest.key === 'PLAN_UPDATE') {
            // Note: Plan updates are metadata for the checklist on the right.
            // They MUST NEVER reset or finalize currentToolId or inLog!
            const prefixLen = nearest.token.length;
            const potentialJson = state.buffer.slice(prefixLen);
            const jsonEnd = findJsonEndIndex(potentialJson);

            if (jsonEnd !== -1) {
                if (potentialJson.length < jsonEnd + 2) {
                    // Wait for trailing ]]
                    break;
                }

                const payloadStr = potentialJson.slice(0, jsonEnd);
                try {
                    const data = JSON.parse(payloadStr);
                    if (nearest.key === 'SET_PLAN') onPlanGenerated?.(data);
                    if (nearest.key === 'PLAN_UPDATE') onPlanUpdate?.(data);
                } catch (e) {
                    console.error('[ZeroDayStreamParser] Plan JSON parse error:', e);
                }

                let advance = prefixLen + jsonEnd;
                while (advance < state.buffer.length && (state.buffer[advance] === ']' || state.buffer[advance] === ' ')) {
                    advance++;
                }
                state.buffer = state.buffer.slice(advance);
                continue;
            } else {
                // Waiting for complete JSON
                break;
            }
        }
    }

    state.inLog = inLog;
    state.currentToolId = currentToolId;
    return newMessages;
};

export const finalizeZeroDayStream = (state, messages) => {
    let newMessages = [...messages];
    const { inLog, currentToolId, buffer, logLineBuffer, currentTextId } = state;

    if (currentToolId) {
        if (logLineBuffer && logLineBuffer.trim()) {
            const lines = logLineBuffer.split('\n').map(l => l.replace(/\r$/, '')).filter(l => l.trim().length > 0);
            newMessages = newMessages.map(m => m.id === currentToolId ? {
                ...m,
                status: 'success',
                logs: [...(m.logs || []), ...lines.map(l => ({ type: 'info', text: l }))]
            } : m);
        } else {
            newMessages = newMessages.map(m => m.id === currentToolId ? { ...m, status: 'success' } : m);
        }
    }

    if (buffer && !inLog) {
        let clean = buffer
            .replace(/<think>[\s\S]*?<\/think>/gi, '')
            .replace(/<\/?(?:call|think|resoaning|reasoning|plan)[^>]*>/gi, '')
            .replace(/call:\w+/gi, '');
        if (clean.trim()) {
            let targetIdx = -1;
            if (currentTextId) {
                targetIdx = newMessages.findIndex(m => m.id === currentTextId);
                if (targetIdx === -1 && newMessages.length > 0) {
                    const lastIdx = newMessages.length - 1;
                    const lastMsg = newMessages[lastIdx];
                    if (lastMsg.id !== 'init-1' && !lastMsg.id.startsWith('init-') && lastMsg.role === 'agent' && (lastMsg.type === 'text' || !lastMsg.type)) {
                        targetIdx = lastIdx;
                    }
                }
            }

            if (targetIdx !== -1) {
                newMessages[targetIdx] = { ...newMessages[targetIdx], content: newMessages[targetIdx].content + clean };
            } else {
                const now = new Date();
                const timeStr = now.toTimeString().split(' ')[0].substring(0, 5);
                newMessages.push({
                    id: `ai-text-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
                    role: 'agent',
                    type: 'text',
                    content: clean,
                    time: timeStr
                });
            }
        }
    }

    state.buffer = '';
    state.inLog = false;
    state.currentToolId = null;
    state.currentTextId = null;
    state.logLineBuffer = '';
    state.isWritingText = false;

    // Guarantee that all tools are marked as completed when stream ends
    newMessages = newMessages.map(m => (m.type === 'tool' && m.status === 'running') ? { ...m, status: 'success' } : m);

    return newMessages;
};